import { useState } from 'react';
import {
  Users,
  Network,
  Fingerprint,
  ChevronDown,
  ChevronRight,
  MapPin,
  Building2,
  Shield,
  Clock,
} from 'lucide-react';
import { useActors } from '../lib/hooks';
import { useAuth } from '../lib/auth-context';
import type { ActorCluster, ActorSessionDetail } from '../lib/api';

/**
 * Actors = IP + 15m activity gap + soft UA clusters above weak fingerprints.
 *
 * Cross-domain limitation: only same-IP hits within 15 minutes merge.
 * An actor returning hours later or from a rotated IP is a new actor.
 */

function formatLocation(city?: string | null, country?: string | null): string | null {
  const c = (city || '').trim();
  const co = (country || '').trim();
  if (c && co) return `${co} · ${c}`;
  if (co) return co;
  if (c) return c;
  return null;
}

function formatAsn(asn?: number | null, org?: string | null): string | null {
  if (asn == null && !(org || '').trim()) return null;
  const parts: string[] = [];
  if (asn != null) parts.push(`AS${asn}`);
  if ((org || '').trim()) parts.push(org!.trim());
  return parts.join(' · ');
}

function threatLevelClass(level?: string): string {
  switch ((level || '').toLowerCase()) {
    case 'critical':
      return 'bg-red-500/15 text-red-300 border-red-500/30';
    case 'high':
      return 'bg-orange-500/15 text-orange-300 border-orange-500/30';
    case 'medium':
      return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
    case 'low':
      return 'bg-green-500/15 text-green-300 border-green-500/30';
    default:
      return 'bg-white/5 text-gray-400 border-white/10';
  }
}

export function Actors() {
  const { isConnected, api } = useAuth();
  const [includeSynthetic, setIncludeSynthetic] = useState(false);
  const { data, isLoading, error } = useActors(includeSynthetic);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<{
    actor: ActorCluster;
    sessions: ActorSessionDetail[];
  } | null>(null);

  const loadDetail = async (id: string) => {
    if (expanded === id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    if (!api) return;
    setExpanded(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await api.getActor(id);
      setDetail(d);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold text-white mb-4">Actors</h2>
        <p className="text-gray-500">Connect to the control plane to view actors.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-400" />
            Actors
          </h2>
          <p className="text-gray-400 mt-1 max-w-2xl text-sm">
            One actor = same exact IP, activity intervals within 15 minutes, soft UA
            compatibility (prefix / browser family). Weak UA fingerprints stay as
            evidence; this is the merge layer so fragmented sessions read as one actor.
          </p>
          <p className="text-amber-500/80 text-xs mt-2 max-w-2xl">
            Cross-domain: only near-simultaneous multi-domain probing (same IP within
            15m) merges. Returning hours later or rotating IPs creates a new actor.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={includeSynthetic}
            onChange={(e) => setIncludeSynthetic(e.target.checked)}
            className="rounded border-white/20 bg-black/40"
          />
          Include synthetic / lab runs
        </label>
      </div>

      {isLoading && <p className="text-gray-500">Loading actors…</p>}
      {error && <p className="text-red-400 text-sm">{String(error)}</p>}

      <div className="space-y-3">
        {(data?.actors || []).map((a) => {
          const listLoc = formatLocation(a.sourceCity, a.sourceCountry);
          const listAsn = formatAsn(a.sourceAsn, a.sourceAsOrg);
          const isOpen = expanded === a.id;
          const actorDetail = isOpen && detail?.actor.id === a.id ? detail.actor : null;
          const loc =
            formatLocation(actorDetail?.sourceCity ?? a.sourceCity, actorDetail?.sourceCountry ?? a.sourceCountry) ||
            listLoc;
          const asn =
            formatAsn(actorDetail?.sourceAsn ?? a.sourceAsn, actorDetail?.sourceAsOrg ?? a.sourceAsOrg) ||
            listAsn;

          return (
            <div
              key={a.id}
              className="rounded-xl border border-white/10 bg-sg-card/60 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => loadDetail(a.id)}
                className="w-full text-left px-4 py-3 flex flex-wrap items-center justify-between gap-3 hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
                  )}
                  <Network className="w-4 h-4 text-blue-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-white truncate">{a.sourceIp}</span>
                      {listLoc && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                          <MapPin className="w-3 h-3 text-gray-500" />
                          {listLoc}
                        </span>
                      )}
                      {a.isSynthetic && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          synthetic
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {a.sessionCount} session{a.sessionCount === 1 ? '' : 's'} ·{' '}
                      {a.requestCount.toLocaleString()} requests · conf {a.maxConfidence}%
                      · depth≤{a.maxDepth} · {a.fingerprintIds.length} fingerprint
                      {a.fingerprintIds.length === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 font-mono shrink-0">
                  {a.firstSeen ? new Date(a.firstSeen).toLocaleString() : '—'}
                  {' → '}
                  {a.lastSeen ? new Date(a.lastSeen).toLocaleString() : '—'}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-white/5 px-4 py-4 space-y-4 bg-black/20">
                  {detailLoading && !detail && (
                    <p className="text-xs text-gray-500">Loading actor detail…</p>
                  )}

                  {/* Summary cards — location first (Overview parity) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                        <MapPin className="w-3 h-3" />
                        Location
                      </div>
                      <div className="text-sm text-white font-medium">
                        {loc || 'Unknown'}
                      </div>
                      {!loc && (
                        <div className="text-[10px] text-gray-600 mt-0.5">
                          No city/country on member sessions
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                        <Building2 className="w-3 h-3" />
                        Network
                      </div>
                      <div className="text-sm text-white font-medium truncate" title={asn || undefined}>
                        {asn || '—'}
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono mt-0.5">{a.sourceIp}</div>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                        <Shield className="w-3 h-3" />
                        Signal
                      </div>
                      <div className="text-sm text-white font-medium">
                        {a.maxConfidence}% conf · depth ≤{a.maxDepth}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        {a.sessionCount} session{a.sessionCount === 1 ? '' : 's'} ·{' '}
                        {a.requestCount.toLocaleString()} reqs
                      </div>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                        <Clock className="w-3 h-3" />
                        Active window
                      </div>
                      <div className="text-xs text-gray-300">
                        {a.firstSeen ? new Date(a.firstSeen).toLocaleString() : '—'}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        → {a.lastSeen ? new Date(a.lastSeen).toLocaleString() : '—'}
                      </div>
                    </div>
                  </div>

                  {a.techniques.length > 0 && (
                    <div className="flex flex-wrap gap-2 text-xs">
                      {a.techniques.map((t) => (
                        <span
                          key={t}
                          className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30 font-mono"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    <Fingerprint className="w-3 h-3 inline mr-1" />
                    fingerprints:{' '}
                    <span className="font-mono text-gray-400">
                      {a.fingerprintIds.map((f) => f.slice(0, 8)).join(', ') || '—'}
                    </span>
                  </div>

                  {detail?.actor.id === a.id && detail.sessions.length > 0 && (
                    <div className="overflow-x-auto -mx-1">
                      <table className="w-full text-xs text-left min-w-[640px]">
                        <thead className="text-gray-500">
                          <tr className="border-b border-white/10">
                            <th className="py-2 pr-3 font-medium">Session</th>
                            <th className="py-2 pr-3 font-medium">Location</th>
                            <th className="py-2 pr-3 font-medium">Level</th>
                            <th className="py-2 pr-3 font-medium">Conf</th>
                            <th className="py-2 pr-3 font-medium">Reqs</th>
                            <th className="py-2 pr-3 font-medium">FP</th>
                            <th className="py-2 font-medium">UA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.sessions.map((s) => {
                            const sLoc = formatLocation(s.sourceCity, s.sourceCountry);
                            return (
                              <tr key={s.id} className="border-t border-white/5 text-gray-300">
                                <td className="py-2 pr-3 font-mono align-top">
                                  {s.id.slice(0, 8)}
                                  {s.isSynthetic && (
                                    <span className="ml-1 text-[9px] text-amber-400/80">lab</span>
                                  )}
                                </td>
                                <td className="py-2 pr-3 align-top">
                                  {sLoc ? (
                                    <span className="inline-flex items-center gap-1 text-gray-300">
                                      <MapPin className="w-3 h-3 text-gray-500 shrink-0" />
                                      {sLoc}
                                    </span>
                                  ) : (
                                    <span className="text-gray-600">—</span>
                                  )}
                                  {s.sourceAsOrg && (
                                    <div className="text-[10px] text-gray-500 mt-0.5 max-w-[10rem] truncate" title={s.sourceAsOrg}>
                                      {s.sourceAsn != null ? `AS${s.sourceAsn} · ` : ''}
                                      {s.sourceAsOrg}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 pr-3 align-top">
                                  <span
                                    className={`inline-block px-1.5 py-0.5 rounded border text-[10px] capitalize ${threatLevelClass(s.threatLevel)}`}
                                  >
                                    {s.threatLevel || '—'}
                                  </span>
                                </td>
                                <td className="py-2 pr-3 align-top tabular-nums">
                                  {s.confidence != null ? `${s.confidence}%` : '—'}
                                </td>
                                <td className="py-2 pr-3 align-top tabular-nums">{s.requestCount}</td>
                                <td className="py-2 pr-3 font-mono align-top">
                                  {s.fingerprintId?.slice(0, 8) || '—'}
                                </td>
                                <td className="py-2 truncate max-w-xs text-gray-400 align-top" title={s.userAgent}>
                                  {s.userAgent || '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {detail?.actor.id === a.id && detail.sessions.length === 0 && (
                    <p className="text-xs text-gray-500">No sessions linked to this actor.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {!isLoading && (data?.actors?.length ?? 0) === 0 && (
          <div className="rounded-xl border border-white/10 bg-sg-card/40 px-4 py-10 text-center text-gray-500 text-sm">
            No actors in this view.
            {!includeSynthetic && (
              <span className="block mt-1 text-gray-600">
                Lab runs are hidden — enable “Include synthetic” to see them.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
